import { useState, useEffect, useCallback } from 'react';
import { NavIcon } from '../../../shell/shared';
import type { PatientTreatmentRecordRow } from '@helios/data-access';
import { useFormatMoney, useRegionalSettings } from '../../../hooks/useFormatMoney';

// ─── Props ───

interface TreatmentRecordDrawerProps {
  open: boolean;
  onClose: () => void;
  primary: string;
  editRecord: PatientTreatmentRecordRow | null;
  onSave: (data: {
    date: string;
    tooth_nos: string;
    procedure_name: string;
    amount_charged_cents: number;
    amount_paid_cents: number;
    next_appointment_at: string;
    notes: string;
    encounter_id: string;
  }) => void;
  encounters: { id: string; label: string }[];
}

// ─── Component ───

export function TreatmentRecordDrawer({
  open,
  onClose,
  primary,
  editRecord,
  onSave,
  encounters,
}: TreatmentRecordDrawerProps) {
  const fmt = useFormatMoney();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;
  const [date, setDate] = useState('');
  const [toothNos, setToothNos] = useState('');
  const [procedureName, setProcedureName] = useState('');
  const [amountCharged, setAmountCharged] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [nextAppt, setNextAppt] = useState('');
  const [notes, setNotes] = useState('');
  const [encounterId, setEncounterId] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editRecord) {
      setDate(editRecord.date ?? '');
      setToothNos(editRecord.tooth_nos ?? '');
      setProcedureName(editRecord.procedure_name ?? '');
      setAmountCharged(String((editRecord.amount_charged_cents ?? 0) / 100));
      setAmountPaid(String((editRecord.amount_paid_cents ?? 0) / 100));
      setNextAppt(editRecord.next_appointment_at?.split('T')[0] ?? '');
      setNotes(editRecord.notes ?? '');
      setEncounterId(editRecord.encounter_id ?? '');
    } else {
      setDate(new Date().toISOString().split('T')[0]);
      setToothNos('');
      setProcedureName('');
      setAmountCharged('');
      setAmountPaid('0');
      setNextAppt('');
      setNotes('');
      setEncounterId('');
    }
  }, [open, editRecord]);

  const handleSave = useCallback(() => {
    if (!procedureName.trim() || !date) return;
    onSave({
      date,
      tooth_nos: toothNos.trim(),
      procedure_name: procedureName.trim(),
      amount_charged_cents: Math.round((parseFloat(amountCharged) || 0) * 100),
      amount_paid_cents: Math.round((parseFloat(amountPaid) || 0) * 100),
      next_appointment_at: nextAppt || '',
      notes: notes.trim(),
      encounter_id: encounterId,
    });
    onClose();
  }, [
    date,
    toothNos,
    procedureName,
    amountCharged,
    amountPaid,
    nextAppt,
    notes,
    encounterId,
    onSave,
    onClose,
  ]);

  if (!open) return null;

  const inputStyle = {
    height: 38,
    padding: '0 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--text)',
    width: '100%',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)' as string,
    color: 'var(--text)',
    marginBottom: 4,
    display: 'block' as const,
  };

  const balance = (parseFloat(amountCharged) || 0) - (parseFloat(amountPaid) || 0);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {/* Backdrop */}
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'relative',
          width: 420,
          background: 'var(--bg-surface)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {editRecord ? 'Edit Treatment Record' : 'Add Treatment Record'}
          </h3>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--bg-surface-2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 'var(--sp-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
          }}
        >
          {/* Date */}
          <div>
            <label style={labelStyle}>
              Date <span style={{ color: 'var(--color-danger-text)' }}>*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Tooth No/s */}
          <div>
            <label style={labelStyle}>Part No/s</label>
            <input
              type="text"
              value={toothNos}
              onChange={(e) => setToothNos(e.target.value)}
              placeholder="#14, #18"
              style={inputStyle}
            />
          </div>

          {/* Procedure */}
          <div>
            <label style={labelStyle}>
              Procedure <span style={{ color: 'var(--color-danger-text)' }}>*</span>
            </label>
            <input
              type="text"
              value={procedureName}
              onChange={(e) => setProcedureName(e.target.value)}
              placeholder="e.g. Composite Filling"
              style={inputStyle}
            />
          </div>

          {/* Amount Charged + Paid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
            <div>
              <label style={labelStyle}>
                Amount Charged <span style={{ color: 'var(--color-danger-text)' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                  }}
                >
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  min={0}
                  value={amountCharged}
                  onChange={(e) => setAmountCharged(e.target.value)}
                  style={{ ...inputStyle, paddingLeft: 24 }}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Amount Paid</label>
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                  }}
                >
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  min={0}
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  style={{ ...inputStyle, paddingLeft: 24 }}
                />
              </div>
            </div>
          </div>

          {/* Balance (computed, read-only) */}
          <div
            style={{
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-surface-2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              Balance
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: balance > 0 ? '#dc2626' : balance < 0 ? '#16a34a' : 'var(--text)',
              }}
            >
              {fmt(balance)}
            </span>
          </div>

          {/* Next Appointment */}
          <div>
            <label style={labelStyle}>Next Appointment</label>
            <input
              type="date"
              value={nextAppt}
              onChange={(e) => setNextAppt(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Encounter (optional) */}
          {encounters.length > 0 && (
            <div>
              <label style={labelStyle}>Encounter (optional)</label>
              <select
                value={encounterId}
                onChange={(e) => setEncounterId(e.target.value)}
                style={{ ...inputStyle, appearance: 'auto' }}
              >
                <option value="">Unassigned</option>
                {encounters.map((enc) => (
                  <option key={enc.id} value={enc.id}>
                    {enc.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={3}
              style={{
                ...inputStyle,
                height: 'auto',
                padding: '10px 12px',
                resize: 'vertical',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-3)',
          }}
        >
          <button
            onClick={onClose}
            style={{
              height: 38,
              padding: '0 20px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!procedureName.trim() || !date}
            style={{
              height: 38,
              padding: '0 24px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: !procedureName.trim() || !date ? 'var(--text-disabled)' : primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: !procedureName.trim() || !date ? 'not-allowed' : 'pointer',
            }}
          >
            {editRecord ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
